import type { BannerItem } from "$root/components/banner-stack/types";
import { ActionText } from "@mdotm/shadcn/legacy/components";
import type { TFunction } from "i18next";
import type { QuantityEditorBuilderProps } from "../builder";
import { checkCounts } from "./checks";

/**
 * Shown while an added instrument has an issue: how many could not be checked — with one action
 * that checks them all again — how many need a fix before the composition can be saved, and how
 * many cannot be traded and will be left out of the save.
 */
export function newInstrumentIssuesBanner({
	instrumentBuilder,
	t,
}: {
	instrumentBuilder: QuantityEditorBuilderProps;
	t: TFunction;
}): BannerItem | null {
	const { failed, needFix, cannotHold } = checkCounts(instrumentBuilder.getComposition().values());
	if (failed === 0 && needFix === 0 && cannotHold === 0) {
		return null;
	}

	return {
		id: "new-instrument-issues",
		severity: "warning",
		title: t("EDIT_COMPOSITION.NEW_INSTRUMENTS.BANNER_TITLE"),
		description: (
			<span data-qualifier="CompositionEditor/NewInstrumentIssuesBanner">
				{failed > 0 && (
					<span className="block">
						{t("EDIT_COMPOSITION.NEW_INSTRUMENTS.BANNER_FAILED", { count: failed })}{" "}
						<ActionText
							data-qualifier="CompositionEditor/NewInstrumentIssuesBanner/RetryAll"
							onClick={() => instrumentBuilder.retryFailedChecks()}
						>
							{t("EDIT_COMPOSITION.NEW_INSTRUMENTS.RETRY_ALL")}
						</ActionText>
					</span>
				)}
				{needFix > 0 && (
					<span className="block">{t("EDIT_COMPOSITION.NEW_INSTRUMENTS.BANNER_NEED_FIX", { count: needFix })}</span>
				)}
				{cannotHold > 0 && (
					<span className="block">
						{t("EDIT_COMPOSITION.NEW_INSTRUMENTS.BANNER_CANNOT_HOLD", { count: cannotHold })}
					</span>
				)}
			</span>
		),
	};
}
